import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BRAND } from 'constants/Parameters';
import { PATH, PATH_NAME } from 'constants/Routes';

import Page404 from 'common/pages/Page404';
import PageLoader from 'common/pages/Loader';
import LazyLoader from 'common/LazyLoader';

import { formatEntity } from 'modules/entity/utils';
import { fetchEntitySuccess } from 'modules/entity/actions';
import { getEntity, hasEntity } from 'modules/entity/selectors';

import { campaignData } from './data';
import OverwriteDrawer from './component/OverwriteDrawer';
import { deepMerge } from './utils';

const AuditPage = React.lazy(() => import('audit/AuditPage'));
const FollowUpPage = React.lazy(() => import('components/followUps/index'));
const AppointmentPage = React.lazy(() => import('appointments/AppointmentPage'));
const InterventionPage = React.lazy(() => import('interventions/InterventionPage'));
const CustomizePage = React.lazy(() => import('components/customize/CustomizePage'));

const PAGES_CONTENT = {
  'follow-up': FollowUpPage,
  audit: AuditPage,
  customize: CustomizePage,
  appointment: AppointmentPage,
  aftersales: InterventionPage,
  sales: InterventionPage,
  parts: InterventionPage,
  campaign: InterventionPage,
};

const TestPage = () => {
  const dispatch = useDispatch();

  /** @type {{ mode: string }} */
  const { mode } = PATH[PATH_NAME.TEST].match(window.location.pathname);

  const data = useSelector(getEntity);
  const hasData = useSelector(hasEntity);
  const [hasFailed, setHasFailed] = React.useState(false);

  const brand = BRAND.normalize();
  const [page, Component] = Object.entries(PAGES_CONTENT).find(
    ([key]) => mode.startsWith(key)
  ) || [];

  const originalDataRef = React.useRef(null);

  const dispatchData = React.useCallback((rawData) => {
    const brandName = rawData.translations?.[`brand.${brand}`] ?? brand;

    dispatch(
      fetchEntitySuccess({
        ...formatEntity(undefined, { ...rawData, ...(page === 'campaign' ? campaignData : {}) }),
        brand: {
          name: brandName,
          id: brand,
          publicLogoPath: `assets/brands/${brand}/logo.png`,
          publicBannerPath: `assets/brands/${brand}/banner.jpg`,
        },
      })
    );
  }, [dispatch, page, brand]);

  React.useEffect(() => {
    (async () => {
      try {
        const { default: importedData } = await import(
          /* webpackMode: "lazy-once" */ `./pages/${mode.replace('campaign-', '')}`
        );
        originalDataRef.current = importedData;
        dispatchData(importedData);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        setHasFailed(true);
      }
    })();
  }, [dispatchData, mode]);

  const handleApply = React.useCallback((override) => {
    if (originalDataRef.current) {
      dispatchData(deepMerge(originalDataRef.current, override));
    }
  }, [dispatchData]);

  const handleReset = React.useCallback(() => {
    if (originalDataRef.current) {
      dispatchData(originalDataRef.current);
    }
  }, [dispatchData]);

  if (!Component || hasFailed) {
    return <Page404>Test page not found</Page404>;
  }

  if (!hasData) {
    return <PageLoader />;
  }

  return (
    <LazyLoader>
      <Component data={data} />
      <OverwriteDrawer
        originalData={originalDataRef.current}
        onApply={handleApply}
        onReset={handleReset}
      />
    </LazyLoader>
  );
};

export default TestPage;
